// Jurisdiction time seam (DEATH-EMAILS-1, s229, D-229.41 ruling 5 of ruling-7).
//
// The death emails print an exact wall-clock moment ("opens at 3:14 p.m. Eastern Time on Thursday,
// 25 September"). Which zone that is depends on where the planner lives, so it resolves HERE from the
// client's jurisdiction columns and never from a hardcoded "Eastern" (jurisdiction seam, CLAUDE.md;
// jurisdiction-architecture.md §6). An unknown or unset jurisdiction falls back to the app's
// canonical wall-clock zone, America/Toronto — the same zone shared/date-format.ts already uses.

export interface JurisdictionZone {
  /** IANA zone used for formatting. */
  timeZone: string;
  /** How the zone is named in plain English in a sentence. */
  label: string;
}

const CANONICAL: JurisdictionZone = { timeZone: "America/Toronto", label: "Eastern Time" };

/** Canadian provinces and territories. A province spanning zones takes its most populous one. */
const CA_ZONES: Record<string, JurisdictionZone> = {
  BC: { timeZone: "America/Vancouver", label: "Pacific Time" },
  YT: { timeZone: "America/Whitehorse", label: "Yukon Time" },
  AB: { timeZone: "America/Edmonton", label: "Mountain Time" },
  NT: { timeZone: "America/Yellowknife", label: "Mountain Time" },
  SK: { timeZone: "America/Regina", label: "Central Time" },
  MB: { timeZone: "America/Winnipeg", label: "Central Time" },
  NU: { timeZone: "America/Iqaluit", label: "Eastern Time" },
  ON: { timeZone: "America/Toronto", label: "Eastern Time" },
  QC: { timeZone: "America/Toronto", label: "Eastern Time" },
  NB: { timeZone: "America/Moncton", label: "Atlantic Time" },
  NS: { timeZone: "America/Halifax", label: "Atlantic Time" },
  PE: { timeZone: "America/Halifax", label: "Atlantic Time" },
  NL: { timeZone: "America/St_Johns", label: "Newfoundland Time" },
};

export function zoneForJurisdiction(country: string | null | undefined, province: string | null | undefined): JurisdictionZone {
  if ((country ?? "CA").toUpperCase() === "CA" && province) {
    return CA_ZONES[province.toUpperCase()] ?? CANONICAL;
  }
  return CANONICAL;
}

/** "3:14 p.m. Eastern Time on Thursday, 25 September" — the board 15 shape (email 20). */
export function formatOpeningMoment(at: Date, zone: JurisdictionZone): string {
  const parts = (opts: Intl.DateTimeFormatOptions) =>
    Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: zone.timeZone, ...opts }).formatToParts(at).map((p) => [p.type, p.value]));
  const t = parts({ hour: "numeric", minute: "2-digit", hour12: true });
  const d = parts({ weekday: "long", day: "numeric", month: "long" });
  const meridiem = (t.dayPeriod ?? "").toLowerCase().replace(/\s/g, "").replace(/^am$/, "a.m.").replace(/^pm$/, "p.m.");
  return `${t.hour}:${t.minute} ${meridiem} ${zone.label} on ${d.weekday}, ${d.day} ${d.month}`;
}

/** The planner's own wait in words: "24 hours", "1 hour", "7 days" (whole days from 48 hours up). */
export function formatWaitLength(hours: number): string {
  const h = Math.max(1, Math.round(hours));
  if (h >= 48 && h % 24 === 0) return `${h / 24} days`;
  return h === 1 ? "1 hour" : `${h} hours`;
}

/** Time left until a moment, for the planner's banner: "3 days 4 hours", "5 hours", "under an hour". */
export function formatTimeLeft(ms: number): string {
  if (ms <= 0) return "moments";
  const totalHours = Math.floor(ms / 3_600_000);
  if (totalHours < 1) return "under an hour";
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const d = days === 1 ? "1 day" : `${days} days`;
  const hr = hours === 1 ? "1 hour" : `${hours} hours`;
  if (days === 0) return hr;
  return hours === 0 ? d : `${d} ${hr}`;
}
